import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { useTheme } from "../components/ThemeContext";
import Select from "react-select";
import axios from "axios";
import "../App.css";

const API_BASE = process.env.REACT_APP_API || "";

const NewEnquiryModal = ({ show, onClose, onSubmit, enquiry }) => {
  const { isDark } = useTheme();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [details, setDetails] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [source, setSource] = useState("");

  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState("");

  const [labelOptions, setLabelOptions] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [labelsError, setLabelsError] = useState("");

  const selectTheme = useMemo(
    () => (theme) => ({
      ...theme,
      colors: {
        ...theme.colors,
        neutral0: isDark ? "#2c2c2c" : "#fff",
        neutral80: isDark ? "#fff" : "#000",
        neutral20: isDark ? "#444" : theme.colors.neutral20,
        neutral30: isDark ? "#666" : theme.colors.neutral30,
        primary: theme.colors.primary,
        primary25: isDark ? "#3a3a3a" : theme.colors.primary25,
      },
    }),
    [isDark],
  );

  useEffect(() => {
    axios
      .get(
        "https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/index.json",
      )
      .then((res) => {
        const options = res.data.map((country) => ({
          value: country.code,
          textLabel: country.name,
          label: (
            <div className="d-flex align-items-center">
              <img
                src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                alt={country.name}
                style={{
                  width: 20,
                  height: 15,
                  marginRight: 10,
                  objectFit: "cover",
                }}
              />
              {country.name}
            </div>
          ),
        }));

        options.sort((a, b) => a.textLabel.localeCompare(b.textLabel));
        setCountryOptions(options);
      })
      .catch((err) => {
        console.error("Failed to load countries:", err);
      });
  }, []);

  useEffect(() => {
    if (!show) return;

    const fetchSources = async () => {
      setSourcesLoading(true);
      setSourcesError("");

      try {
        const res = await axios.get(`${API_BASE}/getSources`);
        const rows = res?.data?.data || [];

        const opts = rows
          .map((r) => ({
            source_id: r.source_id,
            source: r.source,
            color: r.color,
          }))
          .sort((a, b) => String(a.source).localeCompare(String(b.source)));

        setSourceOptions(opts);
      } catch (e) {
        console.error("Failed to load sources", e);
        setSourcesError("Failed to load sources from server.");
        setSourceOptions([]);
      } finally {
        setSourcesLoading(false);
      }
    };

    const fetchLabels = async () => {
      setLabelsLoading(true);
      setLabelsError("");

      try {
        const res = await axios.get(`${API_BASE}/getLabels`);
        const rows = res?.data?.data || [];

        const opts = rows
          .map((r) => ({
            value: r.label_id,
            label: r.label_name,
            description: r.description,
            created_at: r.created_at,
          }))
          .sort((a, b) => String(a.label).localeCompare(String(b.label)));

        setLabelOptions(opts);
      } catch (e) {
        console.error("Failed to load labels", e);
        setLabelsError("Failed to load labels from server.");
        setLabelOptions([]);
      } finally {
        setLabelsLoading(false);
      }
    };

    fetchSources();
    fetchLabels();
  }, [show]);

  useEffect(() => {
    if (enquiry) {
      setName(enquiry.name || "");
      setPhone(enquiry.phone_number || enquiry.phone || "");
      setEmail(enquiry.email || "");
      setDetails(enquiry.enquiry || enquiry.details || "");
      setSource(enquiry.source || "");

      const countryText = enquiry.country || "";

      if (countryText) {
        const match =
          countryOptions.find((c) => c.textLabel === countryText) ||
          countryOptions.find((c) => c.value === countryText);

        setSelectedCountry(
          match || {
            value: countryText,
            textLabel: countryText,
            label: (
              <div className="d-flex align-items-center">
                <span>{countryText}</span>
              </div>
            ),
          },
        );
      } else {
        setSelectedCountry(null);
      }

      const labelId =
        enquiry.label_id || enquiry.labelId || enquiry.labelID || null;
      const labelName =
        enquiry.label_name || enquiry.labelName || enquiry.label || "";

      if (labelId != null) {
        setSelectedLabel({
          value: labelId,
          label: labelName || String(labelId),
        });
      } else if (labelName) {
        setSelectedLabel({ value: labelName, label: labelName });
      } else {
        setSelectedLabel(null);
      }
    } else {
      setName("");
      setPhone("");
      setEmail("");
      setDetails("");
      setSource("");
      setSelectedCountry(null);
      setSelectedLabel(null);
    }
  }, [enquiry, countryOptions]);

  const clearForm = () => {
    setName("");
    setPhone("");
    setEmail("");
    setDetails("");
    setSelectedCountry(null);
    setSource("");
    setSelectedLabel(null);
  };

  const handleSubmit = async () => {
    const countryName = selectedCountry?.textLabel || "";
    const label_name = selectedLabel?.label || "";

    const enquiryData = {
      id: enquiry?.id,
      name,
      phone,
      email,
      details,
      source,
      country: countryName,
      label_name,
    };

    if (!name || !phone || !email || !details || !countryName || !source) {
      alert("All fields are required.");
      return;
    }

    try {
      const url = enquiry
        ? `${API_BASE}/updateEnquire`
        : `${API_BASE}/saveEnquire`;

      const response = await axios.post(url, enquiryData);

      if (response.data.success) {
        alert(enquiry ? "Enquiry updated!" : "Enquiry submitted!");
        onSubmit?.(enquiryData);
        clearForm();
        onClose?.();
      } else {
        alert(response.data.message || "Failed to submit enquiry.");
      }
    } catch (error) {
      console.error("Submission error:", error?.message || error);
      alert("Server error. Failed to submit enquiry.");
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header
        closeButton
        className={isDark ? "bg-dark text-white border-secondary" : ""}
      >
        <Modal.Title>{enquiry ? "Edit Enquiry" : "New Enquiry"}</Modal.Title>
      </Modal.Header>

      <Modal.Body className={isDark ? "bg-dark text-white" : ""}>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter full name"
              className={
                isDark
                  ? "bg-dark text-white border-secondary dark-placeholder"
                  : ""
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
              className={
                isDark
                  ? "bg-dark text-white border-secondary dark-placeholder"
                  : ""
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className={
                isDark
                  ? "bg-dark text-white border-secondary dark-placeholder"
                  : ""
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country</Form.Label>
            <Select
              options={countryOptions}
              value={selectedCountry}
              onChange={(selected) => setSelectedCountry(selected)}
              isSearchable
              placeholder="Search country name..."
              classNamePrefix="react-select"
              theme={selectTheme}
              filterOption={(candidate, input) => {
                const search = input.toLowerCase();
                const countryName = candidate.data.textLabel.toLowerCase();
                return countryName.includes(search);
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <Form.Label className="mb-1">Source</Form.Label>

              {sourcesLoading && (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  <small>Loading sources…</small>
                </div>
              )}
            </div>

            {sourcesError ? (
              <Alert
                variant="danger"
                className="py-2"
                style={{ borderRadius: 12 }}
              >
                {sourcesError}
              </Alert>
            ) : (
              <Form.Select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                disabled={sourcesLoading}
                className={
                  isDark
                    ? "bg-dark text-white border-secondary dark-placeholder"
                    : ""
                }
              >
                <option value="">Select a source</option>

                {sourceOptions.map((item) => (
                  <option key={item.source_id} value={item.source}>
                    {item.source}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <Form.Label className="mb-1">Tag</Form.Label>

              {labelsLoading && (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  <small>Loading labels…</small>
                </div>
              )}
            </div>

            {labelsError ? (
              <Alert
                variant="danger"
                className="py-2"
                style={{ borderRadius: 12 }}
              >
                {labelsError}
              </Alert>
            ) : (
              <Select
                options={labelOptions}
                value={selectedLabel}
                onChange={(v) => setSelectedLabel(v)}
                isSearchable
                placeholder="Select a tag"
                classNamePrefix="react-select"
                theme={selectTheme}
                noOptionsMessage={() =>
                  labelsLoading ? "Loading…" : "No labels found"
                }
              />
            )}

            {selectedLabel?.description ? (
              <div className="text-muted mt-1">
                <small>{selectedLabel.description}</small>
              </div>
            ) : null}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Enter course or enquiry details"
              className={
                isDark
                  ? "bg-dark text-white border-secondary dark-placeholder"
                  : ""
              }
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer className={isDark ? "bg-dark border-secondary" : ""}>
        <Button
          variant={isDark ? "secondary" : "light"}
          onClick={() => {
            onClose?.();
            clearForm();
          }}
        >
          Cancel
        </Button>

        <Button
          variant="outline-light"
          className="btn-color"
          onClick={handleSubmit}
          disabled={labelsLoading || sourcesLoading}
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewEnquiryModal;
